<!-- TOP BAR -->
<header class="topbar">
  <a class="brand" (click)="navigateToRoute('/docs/lca-api')">
    <span class="brand-mark">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="12" r="6" fill="white" fill-opacity="0.95" />
        <circle cx="15" cy="12" r="6" fill="white" fill-opacity="0.55" />
      </svg>
    </span>
    <span class="brand-name">PIQET</span>
    <span class="brand-tag">Docs</span>
  </a>
  <span class="version-select">
    <span class="dot"></span>
    v1.0
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6" /></svg>
  </span>
  <div class="topbar-right">
    <a class="nav-link" (click)="navigateToRoute('/docs/lca-api')">LCA API</a>
    <a class="nav-link" (click)="navigateToRoute('/docs/lca-api/auth')">Auth API</a>
    <a class="nav-link" (click)="navigateToRoute('/docs/quick-reference')">Quick Reference</a>
  </div>
</header>

<div class="layout" *ngIf="apiData">
  <!-- SIDEBAR -->
  <aside class="sidebar">
    <div class="search">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
      <input piqetCtrlKFocus type="text" placeholder="Search reference..." [(ngModel)]="searchQuery" />
      <kbd>Ctrl+K</kbd>
    </div>

    <nav>
      <ng-container *ngFor="let section of filteredNavSections">
        <div class="nav-section">
          <div class="nav-section-title">{{ section.title }}</div>

          <ng-container *ngIf="section.children">
            <div
              class="nav-child"
              *ngFor="let child of section.children"
              [class.active]="child.isActive"
              (click)="setActiveNavChild(child)"
            >
              <span *ngIf="child.method" class="method" [ngClass]="child.methodClass">{{ child.method }}</span>
              {{ child.label }}
            </div>
          </ng-container>

          <ng-container *ngIf="section.items">
            <ng-container *ngFor="let item of section.items">
              <div
                class="nav-item"
                [class.open]="item.isExpanded"
                (click)="toggleNavItem(item)"
              >
                <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6" /></svg>
                {{ item.label }}
              </div>
              <div class="nav-children" [style.display]="item.isExpanded ? 'block' : 'none'">
                <div
                  class="nav-child"
                  *ngFor="let child of item.children"
                  [class.active]="child.isActive"
                  (click)="setActiveNavChild(child); $event.stopPropagation()"
                >
                  <span *ngIf="child.method" class="method" [ngClass]="child.methodClass">{{ child.method }}</span>
                  {{ child.label }}
                </div>
              </div>
            </ng-container>
          </ng-container>
        </div>
      </ng-container>
    </nav>
  </aside>

  <!-- MAIN CONTENT -->
  <main class="content">
    <nav class="breadcrumb fade-in fade-in-1">
      API Reference
      <span class="sep">/</span>
      LCA Calculations
      <span class="sep">/</span>
      <span class="current">Impact Calculator</span>
    </nav>

    <div class="title-row fade-in fade-in-1" id="section-overview">
      <h1 class="page-title">LCA impact <em>calculator.</em></h1>
      <span class="version-badge-large">v1.0</span>
    </div>

    <p class="lede fade-in fade-in-2">{{ apiData.apiInfo.description }}</p>

    <!-- Endpoint URL bar -->
    <div class="endpoint fade-in fade-in-2" id="section-endpoint-url">
      <span class="method method-post">{{ apiData.apiInfo.httpMethod }}</span>
      <span class="path">{{ apiData.apiInfo.baseUrl }}</span>
      <button
        (click)="copyUrl(apiData.apiInfo.baseUrl, $event)"
        [title]="isCopied(apiData.apiInfo.baseUrl) ? 'Copied!' : 'Copy endpoint'"
      >
        <svg *ngIf="!isCopied(apiData.apiInfo.baseUrl)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
        <svg *ngIf="isCopied(apiData.apiInfo.baseUrl)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5" /></svg>
        {{ isCopied(apiData.apiInfo.baseUrl) ? 'Copied' : 'Copy' }}
      </button>
    </div>

    <!-- Security scheme -->
    <div class="security-box fade-in fade-in-2">
      <div class="lock-ico">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
      </div>
      <div>
        <div class="security-title">Security Scheme &middot; bearerAuth</div>
        <p class="security-text">
          This endpoint requires a valid Bearer token. Obtain one via the
          <code>POST /auth/login</code> endpoint. Content type:
          <code>{{ apiData.apiInfo.contentType }}</code>.
        </p>
      </div>
    </div>

    <!-- REQUEST OBJECT SECTIONS -->
    <h2 class="section" id="request-objects">Request objects</h2>
    <p class="section-sub">
      Collapsible schema definitions for each part of the LCA calculation request body.
      Click on linked parameter names to jump to their detailed schema.
    </p>

    <!-- Reusable section macro via ng-template -->
    <ng-template #sectionCard let-sectionKey="sectionKey" let-title="title" let-description="description" let-fields="fields">
      <div
        class="endpoint-card"
        [id]="'section-' + sectionKey"
        [class.open]="isSectionExpanded(sectionKey)"
      >
        <div class="endpoint-head" (click)="toggleSection(sectionKey)">
          <span class="expand-ico">{{ isSectionExpanded(sectionKey) ? '−' : '+' }}</span>
          <span class="endpoint-name">{{ title }}</span>
        </div>
        <div class="endpoint-body" *ngIf="isSectionExpanded(sectionKey)">
          <p class="endpoint-desc" *ngIf="description">{{ description }}</p>

          <div class="fields-label">Parameters</div>
          <div class="ftable">
            <div class="ftable-head">
              <span>Parameter</span><span>Type</span><span>Required</span><span>Description</span>
            </div>
            <div
              class="ftable-row"
              *ngFor="let field of fields"
              [class.selected]="isFieldSelected(field)"
              (click)="selectField(field)"
            >
              <span class="param-name">
                <a
                  *ngIf="shouldShowDetailsLink(field)"
                  class="param-link"
                  (click)="scrollToSection(getSectionForField(field.name)!, $event)"
                >{{ field.name }}</a>
                <span *ngIf="!shouldShowDetailsLink(field)">{{ field.name }}</span>
              </span>
              <span><span class="type-badge" [ngClass]="getDataTypeClass(field.dataType)">{{ field.dataType }}</span></span>
              <span><span class="req-badge" [ngClass]="field.required ? 'req-yes' : 'req-no'">{{ field.required ? 'Required' : 'Optional' }}</span></span>
              <span class="col-desc">
                {{ field.description }}
                <a
                  *ngIf="field.hasMoreDetails"
                  class="ref-link"
                  (click)="goToQuickReference(field.moreDetailsSection, $event)"
                >More details &rarr;</a>
              </span>
            </div>
          </div>

          <div class="param-detail-card" *ngIf="fieldsContainSelected(fields) && selectedField">
            <div class="pd-head">
              <span class="pd-name">{{ selectedField.name }}</span>
              <button class="pd-close" (click)="selectField(selectedField, $event)" title="Close">&times;</button>
            </div>
            <div class="pd-grid">
              <div class="pd-label">Type</div>
              <div class="pd-value">
                <span class="type-badge" [ngClass]="getDataTypeClass(selectedField.dataType)">{{ selectedField.dataType }}</span>
              </div>
              <div class="pd-label">Required</div>
              <div class="pd-value">{{ selectedField.required ? 'Yes' : 'No' }}</div>
              <div class="pd-label">Default</div>
              <div class="pd-value">{{ selectedField.default || '—' }}</div>
              <div class="pd-label">Range</div>
              <div class="pd-value">{{ selectedField.range || '—' }}</div>
              <div class="pd-label">Example</div>
              <div class="pd-value"><code>{{ selectedField.example || '—' }}</code></div>
            </div>
          </div>
        </div>
      </div>
    </ng-template>

    <!-- 1. Main Request Object -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'mainRequestObject',
        title: apiData.sections.mainRequestObject.title,
        description: apiData.sections.mainRequestObject.description,
        fields: apiData.sections.mainRequestObject.fields
      }"
    ></ng-container>

    <!-- 2. Scenario Object -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'scenarioObject',
        title: apiData.sections.scenarioObject.title,
        description: apiData.sections.scenarioObject.description,
        fields: apiData.sections.scenarioObject.fields
      }"
    ></ng-container>

    <!-- 3. Product Details -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'productDetails',
        title: apiData.sections.scenarioObject.productDetailsObject.title,
        description: '',
        fields: apiData.sections.scenarioObject.productDetailsObject.fields
      }"
    ></ng-container>

    <!-- 4. Packing Levels -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'packingLevels',
        title: apiData.sections.packingLevels.title,
        description: apiData.sections.packingLevels.description,
        fields: apiData.sections.packingLevels.fields
      }"
    ></ng-container>

    <!-- 5. Component Details -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'componentDetails',
        title: apiData.sections.packingLevels.componentDetails.title,
        description: '',
        fields: apiData.sections.packingLevels.componentDetails.fields
      }"
    ></ng-container>

    <!-- 6. Transportation Configuration -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'standardTransportation',
        title: apiData.sections.transportation.transportconfiguration.title,
        description: '',
        fields: apiData.sections.transportation.transportconfiguration.fields
      }"
    ></ng-container>

    <!-- 7. Direct Distance -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'directDistance',
        title: apiData.sections.transportation.directDistanceObject.title,
        description: '',
        fields: apiData.sections.transportation.directDistanceObject.fields
      }"
    ></ng-container>

    <!-- 8. Route Segment -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'routeSegment',
        title: apiData.sections.transportation.routeSegmentObject.title,
        description: '',
        fields: apiData.sections.transportation.routeSegmentObject.fields
      }"
    ></ng-container>

    <!-- 9. Filling Details -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'fillingDetails',
        title: apiData.sections.fillingDetails.title,
        description: apiData.sections.fillingDetails.description,
        fields: apiData.sections.fillingDetails.fields
      }"
    ></ng-container>

    <!-- 10. Filling Process -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'fillingProcess',
        title: apiData.sections.fillingDetails.fillingProcessObject.title,
        description: '',
        fields: apiData.sections.fillingDetails.fillingProcessObject.fields
      }"
    ></ng-container>

    <!-- 11. Component Filling Transport -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'componentFillingTransport',
        title: apiData.sections.componentFillingTransportObject.title,
        description: apiData.sections.componentFillingTransportObject.description,
        fields: apiData.sections.componentFillingTransportObject.fields
      }"
    ></ng-container>

    <!-- 12. Distribution Details -->
    <ng-container
      *ngTemplateOutlet="sectionCard; context: {
        sectionKey: 'distributionDetails',
        title: apiData.sections.distributionDetails.title,
        description: apiData.sections.distributionDetails.description,
        fields: apiData.sections.distributionDetails.fields
      }"
    ></ng-container>

    <!-- Callout -->
    <div class="callout">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
      <div>
        <strong>Need reference data?</strong> Check the
        <a class="callout-link" (click)="goToQuickReference('indicators')">Quick Reference</a>
        for available indicators, materials, transport modes, storage types and filling processes.
      </div>
    </div>
  </main>

  <!-- RIGHT CODE PANEL -->
  <aside class="code-panel">
    <!-- Tab bar -->
    <div class="code-card fade-in fade-in-2">
      <div class="code-tabs">
        <div class="code-tab-set">
          <button
            class="code-tab"
            [class.active]="activeTab === 'request'"
            (click)="setActiveTab('request')"
          >Request</button>
          <button
            class="code-tab"
            [class.active]="activeTab === 'response'"
            (click)="setActiveTab('response')"
          >Response</button>
        </div>
        <button class="code-copy" [class.copied]="activeTab === 'request' ? reqCopied : respCopied" (click)="copyCode()">
          <svg *ngIf="!(activeTab === 'request' ? reqCopied : respCopied)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
          <svg *ngIf="(activeTab === 'request' ? reqCopied : respCopied)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5" /></svg>
          {{ (activeTab === 'request' ? reqCopied : respCopied) ? 'Copied' : 'Copy' }}
        </button>
      </div>

      <!-- Response type toggle -->
      <div class="resp-toggle" *ngIf="activeTab === 'response'">
        <button
          class="resp-toggle-btn"
          [class.active]="responseType === 'impact'"
          (click)="setResponseType('impact')"
        >Without Details</button>
        <button
          class="resp-toggle-btn"
          [class.active]="responseType === 'includeDetails'"
          (click)="setResponseType('includeDetails')"
        >Include Details</button>
      </div>

      <!-- View toggle: JSON vs TypeScript -->
      <div class="view-toggle">
        <button
          class="view-toggle-btn"
          [class.active]="codeView === 'json'"
          (click)="setCodeView('json')"
        >JSON</button>
        <button
          class="view-toggle-btn"
          [class.active]="codeView === 'typescript'"
          (click)="setCodeView('typescript')"
        >TypeScript</button>
      </div>

      <!-- Code body -->
      <div class="code-body code-json" *ngIf="codeView === 'json'">
        <div
          class="code-line"
          *ngFor="let line of codeLines"
          [class.active]="selectedField && line.key === selectedField.name"
          [innerHTML]="line.html"
        ></div>
      </div>
      <pre class="code-body code-ts" *ngIf="codeView === 'typescript'">{{ getTypeScriptInterface() }}</pre>
    </div>
  </aside>

  <!-- FOOTER -->
  <footer>
    <div>PIQET &middot; <span class="footer-version">v1.0</span></div>
    <div class="databases">
      <span>REST API</span>
      <span class="sep">&middot;</span>
      <span>JSON</span>
      <span class="sep">&middot;</span>
      <span>OpenAPI 3.1</span>
    </div>
    <div class="status-indicator">
      <span class="dot"></span>
      LCA service operational
    </div>
  </footer>
</div>

<!-- Loading State -->
<div class="loading-container" *ngIf="!apiData">
  <div class="loading-spinner"></div>
  <p>Loading API Documentation...</p>
</div>
